import React, { useState, useEffect, useMemo } from 'react';
import { 
  MessageCircle, 
  Send, 
  Clock, 
  Calendar as CalendarIcon,
  CheckCircle2,
  Phone,
  Mail,
  ChevronDown,
  XCircle,
  RefreshCw,
  Settings as SettingsIcon,
  Search
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useNotifications } from '../context/NotificationContext';
import { sendWhatsAppNotification } from '../utils/whatsapp';
import { intelligentSearch } from '../utils/searchUtils';
import { normalizePhone, isValidMobilePhone } from '../utils/phoneUtils';
import './Reminders.css';
import './Reports.css';

const Reminders = () => {
  const { addNotification } = useNotifications();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const activeStaff = JSON.parse(localStorage.getItem('gdt_active_staff') || '{}');
  const staffName = activeStaff.full_name || '';
  const isAdmin = activeStaff?.type === 'Administrador' || activeStaff?.type === 'Secretaria/o';

  const getTomorrowStr = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const [selectedDate, setSelectedDate] = useState(getTomorrowStr());
  const [selectedProf, setSelectedProf] = useState(isAdmin ? 'VER TODOS' : (staffName || 'VER TODOS'));
  const [selectedStatus, setSelectedStatus] = useState('todos');
  const [searchQuery, setSearchQuery] = useState('');
  const [professionals, setProfessionals] = useState([]);
  const [isSendingAll, setIsSendingAll] = useState(false);

  // Automation settings states
  const [showAutoSettings, setShowAutoSettings] = useState(false);
  const [autoEnabled, setAutoEnabled] = useState(false);
  const [autoProvider, setAutoProvider] = useState('simulator');
  const [autoEndpoint, setAutoEndpoint] = useState('');
  const [autoToken, setAutoToken] = useState('');
  const [autoAppUrl, setAutoAppUrl] = useState(window.location.origin);
  const [autoTemplate, setAutoTemplate] = useState(
    'Hola {paciente}, te recordamos tu turno para mañana {fecha} a las {hora} hs en {sede}. Por favor, confirma tu asistencia ingresando aquí: {link_confirmacion}'
  );

  // Logs state
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const fetchConfig = async () => {
    try {
      const { data, error } = await supabase.from('professionals').select('*').order('full_name', { ascending: true });
      if (error) throw error;
      setProfessionals(data || []);
    } catch (err) {
      console.error("Error fetching professionals for Reminders:", err);
    }
  };

  const fetchReminders = async () => {
    setLoading(true);
    try {
      let query = supabase.from('appointments').select('*');
      
      if (selectedDate) query = query.eq('appointment_date', selectedDate);
      if (selectedProf && selectedProf !== 'VER TODOS') query = query.eq('professional_name', selectedProf);
      if (selectedStatus !== 'todos') {
        if (selectedStatus === 'pendiente') query = query.eq('verification_status', 'not_verified');
        else if (selectedStatus === 'confirmado') query = query.eq('verification_status', 'confirmed');
        else if (selectedStatus === 'rechazado') query = query.eq('verification_status', 'rejected');
        else if (selectedStatus === 'enviado') query = query.eq('verification_status', 'sent');
      }

      const { data, error } = await query.order('appointment_time', { ascending: true });
      if (error) throw error;
      setReminders(data || []);
    } catch (err) {
      console.error(err);
      addNotification('Error al cargar recordatorios', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    setLoadingLogs(true);
    try {
      const { data, error } = await supabase
        .from('whatsapp_logs')
        .select('*')
        .order('sent_at', { ascending: false })
        .limit(10);
      if (error) throw error;
      setLogs(data || []);
    } catch (err) {
      console.error("Error fetching whatsapp logs:", err);
    } finally {
      setLoadingLogs(false);
    }
  };

  const fetchSystemSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('*');
      if (error) throw error;
      if (data && data.length > 0) {
        data.forEach(item => {
          if (item.key === 'wa_auto_enabled') setAutoEnabled(item.value === 'true');
          else if (item.key === 'wa_auto_provider') setAutoProvider(item.value);
          else if (item.key === 'wa_auto_endpoint') setAutoEndpoint(item.value);
          else if (item.key === 'wa_auto_token') setAutoToken(item.value);
          else if (item.key === 'wa_auto_app_url') setAutoAppUrl(item.value);
          else if (item.key === 'wa_auto_template') setAutoTemplate(item.value);
        });
      }
    } catch (err) {
      console.error("Error fetching system settings from database:", err);
    }
  };

  useEffect(() => {
    Promise.resolve().then(() => {
      fetchConfig();
      fetchSystemSettings();
    });
  }, []);

  useEffect(() => {
    Promise.resolve().then(() => {
      fetchReminders();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, selectedProf, selectedStatus]);

  useEffect(() => {
    if (showAutoSettings) {
      Promise.resolve().then(() => {
        fetchLogs();
      });
    }
  }, [showAutoSettings]);

  const handleUpdateStatus = async (id, newStatus) => {
    try {
      const updatePayload = { verification_status: newStatus };
      if (newStatus === 'confirmed') {
        updatePayload.status = 'confirmado';
      } else if (newStatus === 'rejected') {
        updatePayload.status = 'cancelado';
      }

      const { error } = await supabase
        .from('appointments')
        .update(updatePayload)
        .eq('id', id);
      
      if (error) throw error;
      addNotification('Estado de verificación actualizado', 'success');
      fetchReminders();
    } catch (err) {
      console.error("Error updating status:", err);
      addNotification('Error al actualizar estado', 'error');
    }
  };

  const sendWhatsApp = async (r) => {
    const success = await sendWhatsAppNotification(r, autoTemplate, addNotification);
    if (success) {
      fetchReminders();
    }
  };

  const handleSendAll = async () => {
    const list = filteredReminders.filter(r => r.patient_phone);
    if (list.length === 0) {
      addNotification('No hay recordatorios con teléfono válido para enviar.', 'warning');
      return;
    }
    
    setIsSendingAll(true);
    addNotification(`Enviando ${list.length} recordatorios por WhatsApp...`, 'info');
    
    try {
      for (let i = 0; i < list.length; i++) {
        const app = list[i];
        await sendWhatsAppNotification(app, autoTemplate, null);
        if (i < list.length - 1) {
          // Small pause between opens to prevent browser popup blockers from triggering
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      addNotification('Proceso de envío masivo finalizado.', 'success');
      fetchReminders();
    } catch (err) {
      console.error("Error in batch sending:", err);
      addNotification('Ocurrió un error durante el envío masivo.', 'error');
    } finally {
      setIsSendingAll(false);
    }
  };

  const sendSummaryWhatsApp = () => {
    if (!filteredReminders || filteredReminders.length === 0) {
      addNotification('No hay turnos para consolidar en el resumen.', 'error');
      return;
    }

    const dateFormatted = selectedDate.split('-').reverse().join('/');
    let message = `📋 *RESUMEN DE TURNOS - ${dateFormatted}*\n`;
    message += `🩺 *Especialista:* ${selectedProf}\n`;
    message += `📊 *Total:* ${filteredReminders.length} turnos programados\n\n`;

    filteredReminders.forEach((r, idx) => {
      const time = r.appointment_time?.substring(0, 5) || '00:00';
      const statusSymbol = r.verification_status === 'confirmed' ? '✅' : '⏳';
      message += `${idx + 1}. *${time} hs* | ${r.patient_name}\n`;
      message += `   🩺 _${r.study_type}_\n`;
      message += `   📱 Tel: ${r.patient_phone || '---'} (${statusSymbol})\n\n`;
    });

    message += `_GDT - Gestión Digital de Turnos_`;

    const encodedMsg = encodeURIComponent(message);
    const url = `https://wa.me/?text=${encodedMsg}`;
    window.open(url, '_blank');
    addNotification('Resumen consolidado y abriendo WhatsApp...', 'success');
  };

  // Automated WhatsApp Settings Persistency
  const handleSaveAutoSettings = async () => {

    try {
      
    // FIXED: Save to localStorage so frontend logic reads it immediately
    localStorage.setItem('gdt_wa_auto_provider', autoProvider);
    if (autoEndpoint) localStorage.setItem('gdt_wa_auto_endpoint', autoEndpoint);
    if (autoToken) localStorage.setItem('gdt_wa_auto_token', autoToken);
    if (autoTemplate) localStorage.setItem('gdt_wa_auto_template', autoTemplate);
    
    const settingsPayload = [

        { key: 'wa_auto_enabled', value: autoEnabled.toString() },
        { key: 'wa_auto_provider', value: autoProvider },
        { key: 'wa_auto_endpoint', value: autoEndpoint || '' },
        { key: 'wa_auto_token', value: autoToken || '' },
        { key: 'wa_auto_app_url', value: autoAppUrl || window.location.origin },
        { key: 'wa_auto_template', value: autoTemplate }
      ];

      const { error } = await supabase
        .from('system_settings')
        .upsert(settingsPayload, { onConflict: 'key' });

      if (error) throw error;
      addNotification('Configuración guardada en base de datos y programada.', 'success');
    } catch (dbErr) {
      console.error("Database settings save error:", dbErr);
      addNotification('Error al guardar en base de datos: ' + dbErr.message, 'error');
    }
  };

  // Automation Simulator / Cloud Function Trigger
  const runAutomationSimulation = async () => {
    addNotification('Invocando servidor (Edge Function) de Supabase...', 'info');
    try {
      const { data, error } = await supabase.functions.invoke('whatsapp-reminders', {
        method: 'POST'
      });
      
      if (error) throw error;
      
      if (data && data.results) {
        const successCount = data.results.filter(r => r.status === 'success').length;
        const failedItems = data.results.filter(r => r.status !== 'success');
        const reason = failedItems.length > 0 ? ` (${failedItems[0].error || 'Revisa el proveedor de API en Ajustes'})` : '';
        
        if (successCount > 0) {
          addNotification(`Proceso en la nube completado. Éxitos: ${successCount} de ${data.results.length}`, 'success');
        } else {
          addNotification(`Proceso completado. Éxitos: 0 de ${data.results.length}${reason}`, 'warning');
        }
      } else {
        addNotification(data?.message || 'Proceso completado.', 'success');
      }
      
      fetchReminders();
      fetchLogs();
    } catch (err) {
      console.error(err);
      addNotification('Error conectando con Edge Function (verifica que esté desplegada)', 'error');
    }
  };

  // Mass correction function for existing phone numbers across patients and appointments
  const [isFixingPhones, setIsFixingPhones] = useState(false);
  const handleMassFixPhoneNumbers = async () => {
    setIsFixingPhones(true);
    addNotification('Iniciando normalización masiva de números de teléfono...', 'info');
    try {
      // 1. Fetch patients
      const { data: patientsList, error: pErr } = await supabase.from('patients').select('id, full_name, phone');
      if (pErr) throw pErr;

      let patientUpdatedCount = 0;
      for (const pat of (patientsList || [])) {
        if (pat.phone) {
          const cleaned = normalizePhone(pat.phone);
          if (cleaned && cleaned !== pat.phone) {
            await supabase.from('patients').update({ phone: cleaned }).eq('id', pat.id);
            patientUpdatedCount++;
          }
        }
      }

      // 2. Fetch appointments
      const { data: appsList, error: aErr } = await supabase.from('appointments').select('id, patient_phone');
      if (aErr) throw aErr;

      let appointmentUpdatedCount = 0;
      for (const app of (appsList || [])) {
        if (app.patient_phone) {
          const cleaned = normalizePhone(app.patient_phone);
          if (cleaned && cleaned !== app.patient_phone) {
            await supabase.from('appointments').update({ 
              patient_phone: cleaned,
              whatsapp_reminder_status: null, // Reset status so it can retry with fixed phone
              whatsapp_reminder_error: null
            }).eq('id', app.id);
            appointmentUpdatedCount++;
          }
        }
      }

      addNotification(`Normalización completada. Se actualizaron ${patientUpdatedCount} pacientes y ${appointmentUpdatedCount} turnos.`, 'success');
      fetchReminders();
    } catch (err) {
      console.error('Error during mass phone fix:', err);
      addNotification('Error al normalizar teléfonos: ' + err.message, 'error');
    } finally {
      setIsFixingPhones(false);
    }
  };

  // Filter in memory
  const filteredReminders = useMemo(() => {
    if (!searchQuery) return reminders;
    return reminders.filter(r => 
      intelligentSearch(searchQuery, r.patient_name, r.patient_dni)
    );
  }, [reminders, searchQuery]);

  return (
    <div className="reminders-page">
      <div className="page-header">
        <div className="title-section">
          <h1>Recordatorios de Turnos</h1>
          <p>
            Envía confirmaciones por WhatsApp para los turnos de mañana (
            {new Date(new Date().setDate(new Date().getDate() + 1)).toLocaleDateString('es-AR', { day: 'numeric', month: 'long' })}
            ).
          </p>
        </div>
        <div className="header-actions">
          <button 
            className="btn-fix-phones" 
            onClick={handleMassFixPhoneNumbers}
            disabled={isFixingPhones}
            style={{
              background: 'linear-gradient(135deg, #f59e0b, #d97706)',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '0.6rem 1rem',
              fontWeight: 600,
              cursor: isFixingPhones ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px'
            }}
            title="Normaliza los teléfonos agregando el prefijo 549 y corrigiendo los 290+ números mal formateados"
          >
            <Phone size={18} /> {isFixingPhones ? 'CORRIGIENDO TELÉFONOS...' : 'CORREGIR TELÉFONOS (+549)'}
          </button>
          <button className={`btn-settings-auto ${showAutoSettings ? 'active' : ''}`} onClick={() => setShowAutoSettings(!showAutoSettings)}>
            <SettingsIcon size={18} /> AJUSTES AUTOMÁTICOS
          </button>
          <button className="btn-whatsapp-summary" onClick={sendSummaryWhatsApp}>
            <MessageCircle size={18} /> ENVIAR RESUMEN POR WA
          </button>
          <button className="btn-secondary" onClick={() => { addNotification('Sincronizando con WhatsApp...', 'info'); fetchReminders(); }}>
            <RefreshCw size={18} /> SINCRONIZAR
          </button>
          <button 
            className="btn-send-all" 
            onClick={handleSendAll} 
            disabled={isSendingAll}
          >
            <Send size={18} /> {isSendingAll ? 'ENVIANDO...' : 'ENVIAR TODOS'}
          </button>
          <button className="btn-daemon" onClick={runAutomationSimulation}>
            <Send size={18} /> PROBAR SERVIDOR (CRON)
          </button>
        </div>
      </div>

      {showAutoSettings && (
        <div className="automation-settings-panel card glass-panel-premium animate-fade-in">
          <div className="panel-header">
            <h3>⚙️ Configuración de Envío Automático (24 horas antes)</h3>
            <button className="btn-close-panel" onClick={() => setShowAutoSettings(false)}>×</button>
          </div>

          <div className="settings-grid-reminders">
            <div className="settings-col">
              <div className="form-group-auto">
                <label className="toggle-label-auto">
                  <span>Habilitar Envío de Recordatorios de Fondo</span>
                  <label className="switch">
                    <input 
                      type="checkbox" 
                      checked={autoEnabled} 
                      onChange={(e) => setAutoEnabled(e.target.checked)} 
                    />
                    <span className="slider round"></span>
                  </label>
                </label>
              </div>

              <div className="form-group-auto">
                <label>Proveedor de Pasarela WhatsApp API</label>
                <div className="select-wrapper">
                  <select value={autoProvider} onChange={(e) => setAutoProvider(e.target.value)}>
                    <option value="simulator">🤖 Simulador GDT (Modo Pruebas / Logs)</option>
                    <option value="twilio">Twilio API (Canal Oficial)</option>
                    <option value="meta">Meta Cloud API (Oficial / WABA)</option>
                    <option value="evolution">Evolution API (QR WhatsApp Web)</option>
                  </select>
                </div>
              </div>

              {autoProvider !== 'simulator' && (
                <React.Fragment>
                  <div className="form-group-auto">
                    <label>URL Endpoint de API</label>
                    <input 
                      type="text" 
                      value={autoEndpoint} 
                      onChange={(e) => setAutoEndpoint(e.target.value)} 
                      placeholder="Ej: https://api.pasarela.com/v1/messages"
                    />
                  </div>
                  <div className="form-group-auto">
                    <label>Token de Acceso / API Key</label>
                    <input 
                      type="password" 
                      value={autoToken} 
                      onChange={(e) => setAutoToken(e.target.value)} 
                      placeholder="Ingrese token de autenticación"
                    />
                  </div>
                </React.Fragment>
              )}
              
              <div className="form-group-auto">
                <label>URL Base de la Aplicación (Para Links)</label>
                <input 
                  type="text" 
                  value={autoAppUrl} 
                  onChange={(e) => setAutoAppUrl(e.target.value)} 
                  placeholder="Ej: https://miapp.com"
                />
              </div>
            </div>

            <div className="settings-col" style={{ flex: '1.5' }}>
                <div className="form-group-auto">
                  <label>Plantilla de Recordatorio por WhatsApp</label>
                  <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.85rem' }}>
                      <input type="radio" name="tpl" 
                        checked={autoTemplate.includes('Te recordamos el Turno que tienes en GESTION DE TURNOS')}
                        onChange={() => setAutoTemplate(`Hola {paciente},

Te recordamos el Turno que tienes en GESTION DE TURNOS:

📆 Fecha: {fecha}
⏰ Hora: {hora} hs
👉 Profesional: {profesional} .

Datos del Consultorio:
📍 Dirección: Bartolome Mitre 854. CABA
📧 Email: gestionamientodeturnos@gmail.com
☎️ Teléfono: 1125565076
📱 WhatsApp: [[WHATSAPP]]

Para confirmar tu Presencia oprime el siguiente link:
{link_confirmacion}

Para cancelar tu Presencia oprime el siguiente link:
{link_cancelacion}

Se ha asignado el siguiente link de Google Meet para el Turno:
{link_meet}

ATENCIÓN: {mensaje_personalizado_turno}

{mensaje_tipo_turno}

Otra Info: {datos_extra} NO RESPONDA ESTE MENSAJE

Mensaje sobre el Profesional: {mensaje_profesional}`)}
                      />
                      Plantilla Completa
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.85rem' }}>
                      <input type="radio" name="tpl" 
                        checked={autoTemplate === `Hola {paciente}, recordatorio de turno: {fecha} a las {hora} hs con {profesional}. Confirma aquí: {link_confirmacion}`}
                        onChange={() => setAutoTemplate(`Hola {paciente}, recordatorio de turno: {fecha} a las {hora} hs con {profesional}. Confirma aquí: {link_confirmacion}`)}
                      />
                      Plantilla Simple
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.85rem' }}>
                      <input type="radio" name="tpl" 
                        checked={!autoTemplate.includes('Te recordamos el Turno que tienes en GESTION DE TURNOS') && autoTemplate !== `Hola {paciente}, recordatorio de turno: {fecha} a las {hora} hs con {profesional}. Confirma aquí: {link_confirmacion}`}
                        onChange={() => setAutoTemplate('')}
                      />
                      Personalizada
                    </label>
                  </div>
                  
                  <div style={{ background: '#075e54', padding: '10px', borderRadius: '8px 8px 0 0', color: 'white', fontSize: '0.8rem', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Vista Previa / Editor</span>
                    <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>Hacé click en una variable para insertarla</span>
                  </div>
                  <textarea 
                    id="wa-template-editor"
                    rows="12" 
                    value={autoTemplate} 
                    onChange={(e) => setAutoTemplate(e.target.value)}
                    placeholder="Seleccioná una plantilla arriba, o escribí tu mensaje personalizado aquí. Hacé click en las variables de abajo para insertarlas."
                    style={{ borderTop: 'none', borderRadius: '0 0 8px 8px', resize: 'vertical' }}
                  />
                  <div className="template-variables-tags" style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {[
                      ['{paciente}', 'Nombre del paciente'],
                      ['{fecha}', 'Fecha de la cita'],
                      ['{hora}', 'Hora de la cita'],
                      ['{profesional}', 'Profesional'],
                      ['{sede}', 'Nombre de la sede'],
                      ['{sede_direccion}', 'Dirección de la sede'],
                      ['{sede_telefono}', 'Teléfono de la sede'],
                      ['{sede_email}', 'Email de la sede'],
                      ['{link_confirmacion}', 'Link para confirmar'],
                      ['{link_cancelacion}', 'Link para cancelar'],
                      ['{link_meet}', 'Google Meet'],
                      ['{mensaje_personalizado_turno}', 'Mensaje del turno'],
                      ['{mensaje_tipo_turno}', 'Tipo de turno'],
                      ['{datos_extra}', 'Datos extra'],
                      ['{mensaje_profesional}', 'Mensaje del profesional'],
                      ['[[WHATSAPP]]', 'Teléfono WhatsApp'],
                    ].map(([variable, label]) => (
                      <span
                        key={variable}
                        title={`Insertar: ${label}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          const textarea = document.getElementById('wa-template-editor');
                          if (textarea) {
                            const start = textarea.selectionStart;
                            const end = textarea.selectionEnd;
                            const newVal = autoTemplate.substring(0, start) + variable + autoTemplate.substring(end);
                            setAutoTemplate(newVal);
                            // Restore cursor after inserted text
                            setTimeout(() => {
                              textarea.focus();
                              textarea.setSelectionRange(start + variable.length, start + variable.length);
                            }, 10);
                          } else {
                            setAutoTemplate(prev => prev + variable);
                          }
                        }}
                      >
                        {variable}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
          </div>

          <div className="panel-actions-reminders">
            <button className="btn-save-settings" onClick={handleSaveAutoSettings}>
              GUARDAR CONFIGURACIÓN
            </button>
            <button className="btn-simulation-trigger" onClick={runAutomationSimulation}>
              🧪 PROBAR EDGE FUNCTION DE FONDO
            </button>
          </div>

          <div className="automation-logs-section">
            <h4>📋 Registros del Cron de WhatsApp (`whatsapp_logs`)</h4>
            <div className="logs-table-wrapper">
              {loadingLogs ? (
                <div className="loading-logs-text">Cargando registros de auditoría...</div>
              ) : logs.length === 0 ? (
                <div className="no-logs-msg">No se registran envíos automáticos todavía. Haz clic en "Simular Proceso de Fondo" para gatillar recordatorios de prueba.</div>
              ) : (
                <table className="logs-mini-table">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Paciente</th>
                      <th>Teléfono</th>
                      <th>Mensaje Formateado</th>
                      <th style={{ width: '80px', textAlign: 'center' }}>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(log => (
                      <tr key={log.id}>
                        <td>{new Date(log.sent_at).toLocaleString()}</td>
                        <td><strong>{log.patient_name}</strong></td>
                        <td>{log.patient_phone}</td>
                        <td className="log-msg-cell" title={log.message}>{log.message}</td>
                        <td style={{ textAlign: 'center' }}>
                          {log.status === 'success' ? (
                            <span className="log-status-badge success">ÉXITO</span>
                          ) : (
                            <span className="log-status-badge error" title={log.error || 'Fallo API'}>
                              FALLO {log.error ? `(${log.error})` : ''}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="reminders-main-container card glass-panel-premium">
        <div className="blip-filter-bar">
          <div className="blip-filter-item">
            <label>Fecha del Turno</label>
            <div className="blip-input-group">
              <input 
                type="date" 
                value={selectedDate} 
                onChange={(e) => setSelectedDate(e.target.value)} 
              />
              <CalendarIcon size={16} className="blip-icon" />
            </div>
          </div>

          <div className="blip-filter-item">
            <label>Profesional</label>
            <div className="blip-input-group">
              <select 
                value={selectedProf} 
                onChange={(e) => setSelectedProf(e.target.value)}
                disabled={!isAdmin}
              >
                {!isAdmin ? (
                   <option value={staffName}>{staffName}</option>
                ) : (
                  <>
                    <option value="VER TODOS">-- TODOS --</option>
                    {professionals.map(p => <option key={p.id} value={p.full_name || p.name}>{p.full_name || p.name}</option>)}
                  </>
                )}
              </select>
              <ChevronDown size={16} className="blip-icon" />
            </div>
          </div>

          <div className="blip-filter-item">
            <label>Estado Verificación</label>
            <div className="blip-input-group">
              <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                <option value="todos">Todos los Estados</option>
                <option value="pendiente">Pendientes</option>
                <option value="enviado">Enviados</option>
                <option value="confirmado">Confirmados</option>
                <option value="rechazado">Rechazados</option>
              </select>
              <ChevronDown size={16} className="blip-icon" />
            </div>
          </div>

          <div className="blip-filter-item search-filter-item" style={{ flex: 1 }}>
            <label>Buscar Paciente</label>
            <div className="blip-input-group">
              <input 
                type="text" 
                placeholder="Nombre o DNI del paciente..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)} 
                style={{ paddingLeft: '0.75rem', width: '100%' }}
              />
              <Search size={16} className="blip-icon" />
            </div>
          </div>
        </div>

        <div className="audit-results-container">
          <table className="blip-report-table">
            <thead>
              <tr>
                <th style={{ width: '120px' }}>DNI</th>
                <th>Paciente</th>
                <th style={{ width: '150px' }}>Contacto</th>
                <th>Turno / Profesional</th>
                <th style={{ width: '220px' }}>Estado Verificación</th>
                <th style={{ width: '120px' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="text-center-p">Procesando auditoría...</td></tr>
              ) : filteredReminders.length === 0 ? (
                <tr>
                  <td colSpan="6" className="no-data-msg">
                    No hay recordatorios pendientes para los filtros seleccionados.
                  </td>
                </tr>
              ) : filteredReminders.map(r => (
                <tr key={r.id}>
                  <td className="dni-col">{r.patient_dni || '---'}</td>
                  <td>
                    <div className="patient-report-cell">
                      <span className="p-name-bold">{r.patient_name}</span>
                      <span className="p-study-small">{r.study_type}</span>
                    </div>
                  </td>
                  <td>
                    <div className="contact-col">
                      <span className="contact-item"><Phone size={12} /> {r.patient_phone || '---'}</span>
                      <span className="contact-item"><Mail size={12} /> {r.patient_email || '---'}</span>
                    </div>
                  </td>
                  <td>
                    <div className="appointment-info-cell">
                      <span className="app-time-badge">{r.appointment_time?.substring(0,5)} hs</span>
                      <span className="app-prof-name">{r.professional_name}</span>
                    </div>
                  </td>
                  <td>
                    <div className="verification-status-cell">
                      <div className="status-flex">
                        {r.verification_status === 'confirmed' ? (
                          <span className="reminders-status-indicator confirmed">
                            <CheckCircle2 size={14} /> CONFIRMADO
                          </span>
                        ) : r.verification_status === 'rejected' ? (
                          <span className="reminders-status-indicator rejected">
                            <XCircle size={14} /> RECHAZADO
                          </span>
                        ) : r.verification_status === 'sent' ? (
                          <span className="reminders-status-indicator sent">
                            <RefreshCw size={14} /> ENVIADO
                          </span>
                        ) : (
                          <span className="reminders-status-indicator pending">
                            <Clock size={14} /> PENDIENTE
                          </span>
                        )}
                      </div>

                      {/* Automated WhatsApp daemon status badge */}
                      <div className="auto-reminder-status-badge">
                        {r.whatsapp_reminder_status === 'sent' ? (
                          <span className="auto-badge success" title={`Enviado automáticamente el ${r.whatsapp_reminder_sent_at ? new Date(r.whatsapp_reminder_sent_at).toLocaleString() : ''}`}>
                            🤖 Auto-Enviado
                          </span>
                        ) : r.whatsapp_reminder_status === 'failed' ? (
                          <span className="auto-badge failed" title={r.whatsapp_reminder_error || 'Error en envío'}>
                            🤖 Auto-Fallido
                          </span>
                        ) : (
                          <span className="auto-badge pending">
                            🤖 Auto-Pendiente (24h)
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons-reminders">
                      <button className="btn-action-sm wa" onClick={() => sendWhatsApp(r)} title="Enviar WhatsApp Manual">
                        <MessageCircle size={16} />
                      </button>
                      <button className="btn-action-sm check" onClick={() => handleUpdateStatus(r.id, 'confirmed')} title="Marcar Confirmado">
                        <CheckCircle2 size={16} />
                      </button>
                      <button className="btn-action-sm cancel" onClick={() => handleUpdateStatus(r.id, 'rejected')} title="Marcar Rechazado">
                        <XCircle size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Reminders;
